// A read that did not happen, drawn the same way on every surface that can meet
// one.
//
// ONE IMPLEMENTATION RATHER THAN FOUR, and the reason is what the fourth copy
// would have looked like. This block was written for the operator view, when
// that was the only read surface; the directory, the reconciliation report and
// the opt-in report all meet the same five failures, and a screen written in a
// hurry — the morning of a show, by somebody adding the fifth report — is a
// screen that draws a refusal as an empty page of zeroes. That is the one
// mistake this console exists not to make, and it should not be re-decided per
// screen. api/src/reports/routes.ts states the rule for the wire and
// operator/src/access.ts states it for the query; this is where it is drawn.
//
// A REFUSAL IS NEVER DRAWN AS AN EMPTY REPORT. Row-level security answers an
// ungranted reader with no rows rather than an error, so a report that simply
// ran would come back as a clean page of nought; xp/operator throws instead, the
// API turns that into a 403, and this file is the last place in that chain where
// the reasoning could be thrown away. `notCounted` is the sentence that says so,
// and it is a required prop rather than a default so that a new surface cannot
// inherit the operator view's wording about numbers onto a screen that has none.
//
// THE DESIGN'S FORM (TASKS §143, step 5). The page keeps its heading and the
// failure is the kit's Failed card where the content would have been, with
// "Ask again" — which each surface wires to the read that failed. A session
// that has ended is not offered it: the app is already taking the reader to
// sign in.
//
// THE SERVER'S SENTENCE IS PASSED THROUGH VERBATIM. On the 403 a nova_admin
// without a second factor gets, that sentence carries the instruction that
// resolves it — enrol an authenticator and sign in again — and a paraphrase
// would drop it. The console does not know that rule and should not learn it.

import type { ReactNode } from "react";
import { Failed } from "@nova/desk-ui/states";
import type { Failure } from "../api/client.js";

export type RefusalProps = {
  /** The heading. What this surface failed to produce, in its own words. */
  readonly what: string;
  readonly failure: Failure;
  /**
   * The session's role, which decides whether a 403 is offered the enrolment
   * screen — and decides nothing about access.
   *
   * THIS PROP OUTLIVED ITS REASON AND IS KEPT ON A WEAKER ONE. It existed because
   * the console could not tell two 403s apart: `auth.ts` answered
   * `error: "unauthorised"` both for a nova_admin whose session proved no second
   * factor and for a client viewer with no grant, so the difference was in
   * `detail` as prose, and matching on prose breaks the next time somebody
   * improves a sentence.
   *
   * That was fixed on 2026-08-29 and there are now three distinct codes:
   * `second_factor_required` and `role_not_permitted` from `auth.ts`, and
   * `no_access` from `reports/routes.ts`. **A refusal should be read from its
   * code, and this prop should not be the thing deciding anything.**
   *
   * It stays only because the enrolment offer is a question about the person
   * rather than about this refusal — an admin who has not enrolled should be
   * pointed at the screen, and a client viewer never should, whatever went wrong.
   * Reading the code would answer "what was refused"; the role answers "who is
   * standing here". Anything using it for the first question is a defect.
   */
  readonly role: string;
  readonly onEnrol: () => void;
  /** The sentence saying that nothing above is a report. Per surface, because what is absent differs. */
  readonly notCounted: string;
  /** Asks again for whatever failed: the show's own read, or the surface's. */
  readonly onRetry: () => void;
};

export function Refusal({ what, failure, role, onEnrol, notCounted, onRetry }: RefusalProps): ReactNode {
  return (
    <div className="stack">
      <header className="head">
        <h1>{what}</h1>
      </header>
      <Failed
        what={titleFor(failure)}
        detail={failure.detail}
        onRetry={failure.kind === "session_ended" ? undefined : onRetry}
      />

      {/* The way out of the refusal, where there is one this console can offer.
          The sentence above already carries the instruction, and until this
          existed there was nowhere for it to point — which is what made the
          admin path a dead end. Offered beside the server's words rather than
          instead of them: the server is still the one saying what is wrong. */}
      {failure.kind === "refused" && role === "nova_admin" ? (
        <div>
          <div className="acts">
            <button type="button" className="btn" onClick={onEnrol}>
              Set up an authenticator
            </button>
          </div>
          <p className="help help-tight">
            Nova admin reports need an authenticator. If yours is already set up, sign out and sign in
            again.
          </p>
        </div>
      ) : null}

      <p className="help help-tight">{notCounted}</p>
    </div>
  );
}

export function titleFor(failure: Failure): string {
  switch (failure.kind) {
    case "session_ended":
      return "Sign in again";
    case "refused":
      return "You do not have access";
    case "unavailable":
      return "Could not load this";
    case "malformed":
      return "Could not complete the request";
    case "misaddressed":
      return "This link is not valid";
    case "unreachable":
      return "Could not connect";
  }
}
